/**
 * @fileoverview AR-805 — translation between the backend payloads and the
 * domain shapes, plus normalization of the backend's 422 error envelopes.
 * Split from httpAdapter.ts so each file stays focused.
 *
 * Contract: mosaic-backend/docs/frontend-integration/google-workspace-connection.md
 * and google-classroom-course-link.md. Payloads arrive camelCased (humps).
 */

import {
  ClassroomPortError,
  ProjectLinkResult,
  ProjectLinkState,
  RosterStudent,
  RosterSummary,
  RosterUnmatchedStudent,
  SchoolConnection,
  SchoolConnectionStatus,
} from '../types'

interface ApiCourse {
  id: string
  googleCourseId: string
  name: string
  alternateLink: string | null
  origin: 'created' | 'linked'
  syncEnabled: boolean
  mappedStudentsCount: number
  lastRosterSyncAt: string | null
  /** Status of the USER connection backing the link ('disconnected' = the
   *  tutor who linked disconnected; the link survives but stops syncing). */
  connectionStatus?: SchoolConnectionStatus
  connectionGoogleEmail?: string | null
}

interface ApiCardPayload {
  linked: boolean
  course?: ApiCourse
  roster?: {
    mappedStudentsCount: number
    students: RosterStudent[]
    /** Unmatched project students (no reason in the GET). */
    unmatched?: RosterUnmatchedStudent[]
  }
}

interface ApiMutationPayload {
  course: ApiCourse
  roster?: RosterSummary | null
  /** Set when the link was kept but the matching run failed. */
  rosterError?: string
}

/**
 * The shared axios interceptor RESOLVES 401 responses instead of rejecting
 * (helpers/api.ts), so every call must check the status explicitly — otherwise
 * an unauthorized profile would read the error body as if it were data.
 */
export function ensureOk<T>(response: { status: number; data: T }): T {
  if (response.status === 401) {
    throw new ClassroomPortError('unauthorized', 'Not allowed to manage this integration')
  }
  return response.data
}

/** First `{ field: ["message"] }` pair of a Rails-style error envelope. */
function firstMessage(payload: unknown): { field: string; message: string } | null {
  if (!payload || typeof payload !== 'object') return null
  for (const [field, value] of Object.entries(payload as Record<string, unknown>)) {
    if (Array.isArray(value) && typeof value[0] === 'string') return { field, message: value[0] }
  }
  return null
}

/** Maps the backend's 422 field+message pairs to normalized error codes. */
export function normalizeError(error: unknown): ClassroomPortError {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response
  const detail = firstMessage(response?.data)
  if (!detail) return new ClassroomPortError('classroom_api_error')

  const message = detail.message
  const lower = message.toLowerCase()
  // 422 course_id — 1 course = 1 project. NOTE: error bodies do NOT go
  // through the humps interceptor (it only camelizes on success), so the
  // primary check is snake_case; camelCase kept as defense.
  if (detail.field === 'course_id' || detail.field === 'courseId') {
    if (lower.includes('already linked')) return new ClassroomPortError('course_already_linked', message)
    if (lower.includes('not found')) return new ClassroomPortError('course_not_found', message)
  }
  if (detail.field === 'connection') {
    // "The Google Workspace connection behind this link is gone — link again":
    // the tutor who linked disconnected. Telling the caller to connect their
    // own account would not fix it — the link must be redone.
    if (lower.includes('behind this link')) return new ClassroomPortError('connection_gone', message)
    // "must be reconnected to grant roster permissions" / "needs reconnection"
    if (lower.includes('reconnect')) return new ClassroomPortError('needs_reconnection', message)
    // "Google Classroom API error <status>": the caller IS connected — Google
    // itself refused (account without Classroom, insufficient rights, quota).
    // Saying "not connected" here would send the user down the wrong path.
    if (lower.includes('api error')) return new ClassroomPortError('classroom_api_error', message)
    return new ClassroomPortError('not_connected', message)
  }
  if (lower.includes('already linked')) return new ClassroomPortError('already_linked', message)
  if (lower.includes('not linked')) return new ClassroomPortError('not_linked', message)
  if (lower.includes('not found')) return new ClassroomPortError('course_not_found', message)
  if (lower.includes('disabled')) return new ClassroomPortError('sync_disabled', message)
  return new ClassroomPortError('classroom_api_error', message)
}

export function toConnection(data: Partial<SchoolConnection>): SchoolConnection {
  return {
    connected: Boolean(data.connected),
    // The backend omits `status` when the user was never connected.
    status: data.status || 'disconnected',
    googleEmail: data.googleEmail,
    workspaceDomain: data.workspaceDomain,
    connectedAt: data.connectedAt,
  }
}

export function toLinkState(card: ApiCardPayload, connection: SchoolConnection): ProjectLinkState {
  const course = card.course
  const linked = Boolean(card.linked && course)
  return {
    // A linked card reports the status of the connection BACKING the link
    // (which may belong to another tutor); an unlinked card reports the
    // caller's own connection, which is what gates the link modal. When the
    // backend does not send the per-link field yet (front deployed first),
    // a linked card defaults to 'connected' — never a false orphan alert.
    connectionStatus: linked ? course?.connectionStatus ?? 'connected' : connection.status,
    linked,
    syncEnabled: course?.syncEnabled ?? false,
    courseId: course?.googleCourseId ?? null,
    courseName: course?.name ?? null,
    courseLink: course?.alternateLink ?? null,
    origin: course?.origin ?? null,
    mappedStudentCount: course?.mappedStudentsCount ?? 0,
    lastRosterSyncAt: course?.lastRosterSyncAt ?? null,
    rosterStudents: card.roster?.students,
    unmatchedStudents: card.roster?.unmatched,
  }
}

export function toLinkResult(payload: ApiMutationPayload): ProjectLinkResult {
  const course = payload.course
  return {
    linked: true,
    syncEnabled: course.syncEnabled,
    courseId: course.googleCourseId,
    courseName: course.name,
    courseLink: course.alternateLink,
    // null only when the matching run failed — the link itself is kept
    // (rosterError says why).
    roster: payload.roster ?? null,
    rosterError: payload.rosterError ?? null,
  }
}
